import { CommonModule } from '@angular/common';

import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  QaiButtonComponent,
  QaiFieldComponent,
  QaiFloatingSelectComponent,
  QaiIconComponent,
  QaiInputComponent,
  QaiLabelComponent,
  QaiTrimLeadingDirective,
  QaiFileUploadComponent,
  QaiLoadingComponent,
} from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { IAvatarReplica } from 'src/app/interfaces/self-serve/responses/iavatarReplicas';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { firstValueFrom, merge, forkJoin, throwError } from 'rxjs';
import { VideoOption } from 'src/app/interfaces/self-serve/ivideo-option';
import { NoCacheSrcDirective } from 'src/app/directives/no-cache-src.directive';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'personal-information',
  standalone: true,
  templateUrl: './persona-information.component.html',
  styleUrls: ['./persona-information.component.scss'],
  imports: [
    QaiFieldComponent,
    QaiLabelComponent,
    QaiInputComponent,
    QaiButtonComponent,
    FormsModule,
    CommonModule,
    QaiLoadingComponent,
    QaiIconComponent,
    DetailsSectionComponent,
    QaiFloatingSelectComponent,
    ReactiveFormsModule,
    QaiTrimLeadingDirective,
    QaiFileUploadComponent,
    NoCacheSrcDirective,
  ],
})
export class PersonaInformationComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  callPersonaGroup!: FormGroup;
  private avatarReplicas: IAvatarReplica[] | undefined;
  callUuid: string = '';
  readOnly: boolean = false;
  isReady: boolean = false;
  options: VideoOption[] | undefined;
  private isRequestInProgress: boolean = false;
  private pendingSaveRequest: boolean = false;
  private uploadBackgroundInProgress: boolean = false;
  private previousReplicaId: string | undefined = undefined;
  private initialReplicaId: string | null = null;
  initialGreetingDescription: string =
    'The persona will start the simulation with one of these greetings. You can have a maximum of 5 options.';
  resumeMessageDescription: string =
    'When a learner resumes a simulation after a pause, the persona will begin with one of these greetings. You can have a maximum of 5 options.';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private featureFlagsService: FeatureFlagsService
  ) {}

  avatarReplicasSelectOptions: VideoOption[] | undefined;
  avatarReplicaSelected: string | undefined;
  selectedAvatarThumbnail: string | undefined;
  selectedAvatar: string | undefined;
  uploadedBackgroundImage: string | null = null; 
  uploadedBackgroundFile: File | null = null;
  showFileUpload = true;
  selectedAvatarGreenThumbnail: string | null = null;
  private hasPendingBackgroundUpload: boolean = false;
  private hasUserChangedAvatar: boolean = false;
  @ViewChild('chromaKeyCanvas', { static: false }) chromaKeyCanvas?: ElementRef<HTMLCanvasElement>;
  processedImageDataUrl: string | null = null;
  isUploadingBackground: boolean = false;
  uploadError: string | null = null;
  backgroundUploadSuccess: boolean = false;
  isDeletingBackground: boolean = false;
  deleteError: string | null = null;
  showAvatarCustomThumbnail: boolean = false;

  async ngOnInit() {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.showAvatarCustomThumbnail = await this.featureFlagsService.isFlagEnabled(AppConstants.AVATAR_CUSTOM_THUMBNAIL);
    this.initializeFormAndReplicas();
  }

  ngOnDestroy(): void {
    this.isRequestInProgress = false;
    this.pendingSaveRequest = false;
    this.uploadBackgroundInProgress = false;
  }

  get welcomeMessageArray(): FormArray {
    return this.getMessageArray(SimulationConstants.WELCOME_MESSAGE_SECTION);
  }

  get resumeMessageArray(): FormArray {
    return this.getMessageArray(SimulationConstants.RESUME_MESSAGE_SECTION);
  }

  get welcomeMessageControls() {
    return this.welcomeMessageArray.controls;
  }

  get resumeMessageControls() {
    return this.resumeMessageArray.controls;
  }

  get isAddGreetingDisabled(): boolean {
    return this.isAddDisabled(this.welcomeMessageArray);
  }

  get isAddResumeMessageDisabled(): boolean {
    return this.isAddDisabled(this.resumeMessageArray);
  }

  private async initializeFormAndReplicas(): Promise<void> {
    try {
      await this.loadForm();
      this.setReadOnlyMode();
      await this.loadReplicas();
      this.setSelectedAvatarFromForm();
      this.initialReplicaId = this.form.get('replicaId')?.value || null;
      this.previousReplicaId = this.form.get('replicaId')?.value;
      this.loadCustomBackgroundFromForm();
      if (this.avatarReplicaSelected && this.uploadedBackgroundImage) {
        setTimeout(() => this.processChromaKey(), 100);
      }
      
      this.isReady = true;
      this.subscribeToFormChanges();
    } catch (error) {
      console.error('Failed to initialize form or replicas:', error);
    }
  }

  private setReadOnlyMode() {
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.form.get('personaName')
    );
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.form.get('welcomeMessage') as FormArray
    );
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.form.get('resumeMessage') as FormArray
    );
  }

  private async loadForm(): Promise<void> {
    const form = await firstValueFrom(
      this.authorReactiveFormService.getForm(this.callUuid)
    );
    this.form = form.get(SimulationConstants.CALL_PERSONA_PATH) as FormGroup;
  }

  private async loadReplicas(): Promise<void> {
    const replicas: IAvatarReplica[] = await firstValueFrom(
      this.authorService.getAvatarReplicas()
    );
    this.avatarReplicas = replicas;
    this.avatarReplicasSelectOptions =
      this.convertReplicasToSelectOptions(replicas);
  }

  private setSelectedAvatarFromForm(): void {
    const currentReplicaId = this.form.get('replicaId')?.value;
    const matched = this.avatarReplicasSelectOptions?.find(
      (option) => option.value === currentReplicaId
    );

    if (matched) {
      this.selectedAvatar = matched.value;
      this.avatarReplicaSelected = matched.videoUrl;
      if (!this.hasUserChangedAvatar) {
        const avatarUrl = this.form.get('avatarUrl')?.value;
        if (avatarUrl) {
          this.selectedAvatarThumbnail = avatarUrl;
        } else {
          this.selectedAvatarThumbnail = matched.photoUrl;
        }
      } else {
        this.selectedAvatarThumbnail = matched.photoUrl;
      }
      
      this.form.patchValue({ replicaId: matched.value }, { emitEvent: false });
      
      if (this.uploadedBackgroundImage && matched.photoUrl) {
        this.selectedAvatarGreenThumbnail = this.getGreenScreenAvatarUrl(matched.photoUrl);
      }
    } else {
      const avatarUrl = this.form.get('avatarUrl')?.value;
      
      if (avatarUrl) {
        this.selectedAvatarThumbnail = avatarUrl;
      }
      
      this.selectedAvatar = undefined;
      this.avatarReplicaSelected = undefined;
    }
  }

  private loadCustomBackgroundFromForm(): void {
    const customBackgroundUrl = this.form.get('customBackgroundImage')?.value;
    if (customBackgroundUrl) {
      this.uploadedBackgroundImage = customBackgroundUrl;
      if (this.selectedAvatarThumbnail) {
        this.selectedAvatarGreenThumbnail = this.getGreenScreenAvatarUrl(this.selectedAvatarThumbnail);
      }
    }
  }

  private subscribeToFormChanges(): void {
    const personaNameControl = this.form.get('personaName');
    const replicaIdControl = this.form.get('replicaId');
    const welcomeMessageControl = this.form.get('welcomeMessage');
    const resumeMessageControl = this.form.get('resumeMessage');

    if (
      personaNameControl &&
      replicaIdControl &&
      welcomeMessageControl &&
      resumeMessageControl
    ) {
      merge(
        personaNameControl.valueChanges,
        replicaIdControl.valueChanges,
        welcomeMessageControl.valueChanges,
        resumeMessageControl.valueChanges
      )
        .pipe(
          debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
          distinctUntilChanged()
        )
        .subscribe(() => {
          if (this.isPersonaComplete()) {
            this.savePersona();
          }

          this.setSelectedAvatarFromForm();
        });
    }
  }

  convertReplicasToSelectOptions(replicas: IAvatarReplica[]): VideoOption[] {
    return replicas.map((replica, index) => ({
      value: replica.replica_id,
      label: replica.replica_name,
      videoUrl: replica.thumbnail_video_url,
      photoUrl: replica.thumbnail_s3_url,
    }));
  }

  avatarReplicaSelectionChange(selectedAvatar: VideoOption | undefined): void {
    if (!selectedAvatar) {
      return;
    }

    this.hasUserChangedAvatar = true;
    const currentReplicaId = this.form.get('replicaId')?.value;
    
    this.form.patchValue(
      { replicaId: selectedAvatar.value },
      { emitEvent: true }
    );

    this.avatarReplicaSelected = selectedAvatar.videoUrl;
    this.selectedAvatarThumbnail = selectedAvatar.photoUrl;
    const isFirstAvatarSelection = !this.initialReplicaId && !currentReplicaId;
    
    if (isFirstAvatarSelection && this.uploadedBackgroundImage && this.uploadedBackgroundFile) {
      this.selectedAvatarGreenThumbnail = this.getGreenScreenAvatarUrl(
        selectedAvatar.photoUrl || ''
      );
      
      setTimeout(() => {
        this.processChromaKey();
      }, 100);
    } else if (this.initialReplicaId || currentReplicaId) {
      this.clearCustomBackgroundState();
    }
  }

  addGreetingOption(): void {
    this.addMessage(SimulationConstants.WELCOME_MESSAGE_SECTION);
    console.log('Added greeting option');
  }

  addResumeMessage(): void {
    this.addMessage(SimulationConstants.RESUME_MESSAGE_SECTION);
    console.log('Added resume message');
  }

  removeGreetingOption(index: number): void {
    this.removeMessage(SimulationConstants.WELCOME_MESSAGE_SECTION, index);
  }

  removeResumeMessage(index: number): void {
    this.removeMessage(SimulationConstants.RESUME_MESSAGE_SECTION, index);
  }

  private getMessageArray(key: string): FormArray {
    return this.form.get(key) as FormArray;
  }

  private addMessage(key: string): void {
    const array = this.getMessageArray(key);
    if (array.length >= 5) return;
    array.push(this.fb.control('', Validators.required));
  }

  private removeMessage(key: string, index: number): void {
    this.getMessageArray(key).removeAt(index);
    console.log(`Removed ${key} at index ${index}`, this.form);
  }

  private isAddDisabled(messageArray: FormArray): boolean {
    const lastControl = messageArray.at(messageArray.length - 1);
    return !lastControl || !lastControl.value?.trim();
  }

  private buildCreatePayload() {
    return {
      mode: 'custom',
      callUuid: this.callUuid,
      personaName: this.form.get('personaName')?.value,
      replicaId: this.form.get('replicaId')?.value,
      welcomeMessage: this.form.get('welcomeMessage')?.value,
      resumeMessage: this.form.get('resumeMessage')?.value,
      thumbnailUrl: this.form.get('customThumbnailUrl')?.value || '',  
      backgroundImageUrl: this.form.get('customBackgroundUrl')?.value || '',
      useWithNewTranscription: true,
    };
  }

  private buildUpdatePayload(personaId: string) {
    const currentReplicaId = this.form.get('replicaId')?.value;
    const hasReplicaIdChanged = currentReplicaId !== this.previousReplicaId;
    
    return {
      personaId,
      callUuid: this.callUuid,
      personaName: this.form.get('personaName')?.value,
      replicaId: currentReplicaId,
      welcomeMessage: this.form.get('welcomeMessage')?.value,
      resumeMessage: this.form.get('resumeMessage')?.value,
      thumbnailUrl: this.form.get('customThumbnailUrl')?.value || '',  
      backgroundImageUrl: this.form.get('customBackgroundUrl')?.value || '',
      useWithNewTranscription: true,
    };
  }

  savePersona(): void {
    if (this.isRequestInProgress) {
      this.pendingSaveRequest = true;
      return;
    }

    if (this.uploadBackgroundInProgress) {
      this.pendingSaveRequest = true;
      return;
    }

    this.isRequestInProgress = true;
    this.pendingSaveRequest = false;

    const personaId = this.form.get('personaId')?.value;
    const request$ = personaId
      ? this.authorService.updatePersonaInfo(this.buildUpdatePayload(personaId))
      : this.authorService.createPersonaInfo(this.buildCreatePayload());

    request$.subscribe({
      next: (response) => {
        console.log(
          `Persona ${personaId ? 'updated' : 'created'} successfully`,
          response
        );

        this.form.patchValue(
          {
            uuid: response.uuid ?? '',
            personaId: response.personaId,
            replicaId: response.replicaId,
          },
          { emitEvent: false }
        );
        this.form.updateValueAndValidity({ onlySelf: true });
        this.previousReplicaId = this.form.get('replicaId')?.value;
        this.isRequestInProgress = false;
        
        const hasUploadedUrls = 
          this.form.get('customThumbnailUrl')?.value && 
          this.form.get('customBackgroundUrl')?.value;
        
        if (!personaId && hasUploadedUrls && !this.uploadBackgroundInProgress) {
          this.updatePersonaWithThumbnail();
        }
        
        if (this.hasPendingBackgroundUpload) {
          this.uploadBackgroundAndThumbnail();
        }
        
        if (this.pendingSaveRequest) {
          setTimeout(() => this.savePersona(), 0);
        }
      },
      error: (err) => {
        console.error(
          `Error ${personaId ? 'updating' : 'creating'} persona`,
          err
        );
        this.isRequestInProgress = false;
        
        if (this.pendingSaveRequest) {
          setTimeout(() => this.savePersona(), 0);
        }
      },
    });
  }

  private isPersonaComplete(): boolean {
    return this.form.valid;
  }

  // Method to reset the request flags (useful for testing or manual reset)
  resetRequestFlags(): void {
    this.isRequestInProgress = false;
    this.pendingSaveRequest = false;
    this.uploadBackgroundInProgress = false;
  }

  /**
   * Converts a data URL to a File object
   */
  private dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  }

  private updatePersonaWithThumbnail(): void {
    const personaId = this.form.get('personaId')?.value;
    if (!personaId) {
      console.warn('No personaId found, skipping update');
      return;
    }
    
    const payload = this.buildUpdatePayload(personaId);
    
    this.authorService.updatePersonaInfo(payload).subscribe({
      next: (response) => {
        console.log('Persona updated with custom thumbnail/background', response);

        this.form.patchValue(
          { uuid: response.uuid ?? '' },
          { emitEvent: false }
        );
      },
      error: (err) => {
        console.error('Error updating persona with thumbnail', err);
      }
    });
  }

  private uploadBackgroundAndThumbnail(): void {
    if (!this.uploadedBackgroundFile || !this.processedImageDataUrl) {
      return;
    }
    
    this.isUploadingBackground = true;
    this.uploadBackgroundInProgress = true;
    this.uploadError = null;
    this.backgroundUploadSuccess = false;
    
    const thumbnailFile = this.dataURLtoFile(
      this.processedImageDataUrl,
      `processed-thumbnail-${Date.now()}.jpg`
    );
    
    if (!thumbnailFile || thumbnailFile.size === 0) {
      const errorMsg = 'Invalid thumbnail file generated!';
      this.uploadError = errorMsg;
      this.isUploadingBackground = false;
      this.hasPendingBackgroundUpload = false;
      return;
    }
    
    const backgroundUpload$ = this.authorService.createCallBackground(
      this.callUuid,
      this.uploadedBackgroundFile
    );
    
    const thumbnailUpload$ = this.authorService.createCallThumbnail(
      this.callUuid,
      thumbnailFile
    );
    
    backgroundUpload$.pipe(
      switchMap((backgroundUrl) => {
        return thumbnailUpload$.pipe(
          map((thumbnailUrl) => ({
            backgroundUrl,
            thumbnailUrl
          }))
        );
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    ).subscribe({
      next: (result) => {
        this.isUploadingBackground = false;
        this.uploadBackgroundInProgress = false;
        this.backgroundUploadSuccess = true;
        this.hasPendingBackgroundUpload = false;
        this.uploadError = null;

        this.form.patchValue({ 
          customBackgroundUrl: result.backgroundUrl,
          customThumbnailUrl: result.thumbnailUrl,
          avatarUrl: result.thumbnailUrl  
        }, { emitEvent: false });
        
        const personaId = this.form.get('personaId')?.value;
        if (personaId) {
          this.updatePersonaWithThumbnail();
        } 
        else if (this.pendingSaveRequest) {
          setTimeout(() => this.savePersona(), 0);
        } else {
          console.log('No persona yet and no pending request.');
        }
      },
      error: (err) => {
        this.isUploadingBackground = false;
        this.uploadBackgroundInProgress = false;
        this.hasPendingBackgroundUpload = false;
        this.uploadError = 'Failed to upload Image. Please try again.';
        this.uploadedBackgroundImage = null;
        this.uploadedBackgroundFile = null;
        this.processedImageDataUrl = null;
        
        this.form.patchValue({ 
          customBackgroundImage: null 
        }, { emitEvent: false });
        
        if (this.pendingSaveRequest) {
          setTimeout(() => this.savePersona(), 0);
        }
      }
    });
  }

  private getGreenScreenAvatarUrl(thumbnailUrl: string): string {
    const lastSlashIndex = thumbnailUrl.lastIndexOf('/');
    return thumbnailUrl.substring(0, lastSlashIndex + 1) + 'green-' + thumbnailUrl.substring(lastSlashIndex + 1);
  }

  onFileSelected(file: File): void {
    console.log('File selected:', file);
    this.handleBackgroundImageUpload(file);
  }

  onFileDropped(file: File): void {
    console.log('File dropped:', file);
    this.handleBackgroundImageUpload(file);
  }

  private handleBackgroundImageUpload(file: File): void {
    this.uploadedBackgroundFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.uploadedBackgroundImage = e.target?.result as string;
      
      this.form.patchValue(
        { customBackgroundImage: this.uploadedBackgroundImage },
        { emitEvent: true }
      );

      if (this.selectedAvatar && this.avatarReplicas) {
        const selectedReplica = this.avatarReplicas.find(
          r => r.replica_id === this.selectedAvatar
        );
        if (selectedReplica?.thumbnail_s3_url) {
          this.selectedAvatarGreenThumbnail = this.getGreenScreenAvatarUrl(
            selectedReplica.thumbnail_s3_url
          );
        }
      }

      if (this.avatarReplicaSelected) {
        setTimeout(() => this.processChromaKey(), 100);
      }
    };

    reader.onerror = (error) => {
      console.error('Error uploading file. Please try again.', error);
    };

    reader.readAsDataURL(file);
  }

  removeCustomBackground(): void {
    const personaId = this.form.get('personaId')?.value;
    
    this.clearCustomBackgroundState();
    
    if (personaId) {
      this.updatePersonaWithThumbnail();
    }
    
    this.setSelectedAvatarFromForm();
  }

  private clearCustomBackgroundState(): void {
    this.uploadedBackgroundImage = null;
    this.uploadedBackgroundFile = null;
    this.processedImageDataUrl = null;
    this.selectedAvatarGreenThumbnail = null;
    this.hasPendingBackgroundUpload = false;
    this.backgroundUploadSuccess = false;
    this.uploadError = null;
    this.deleteError = null;
    
    this.form.patchValue({ 
      customBackgroundImage: null,
      customBackgroundUrl: '',  
      customThumbnailUrl: '',  
      avatarUrl: '',  
    }, { emitEvent: true });
    
    this.showFileUpload = false;
    setTimeout(() => {
      this.showFileUpload = true;
    }, 0);
  }

  private async processChromaKey(): Promise<void> {
    if (!this.uploadedBackgroundImage || !this.avatarReplicaSelected || !this.selectedAvatarGreenThumbnail) {
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      const [avatarImg, backgroundImg] = await Promise.all([
        this.loadImage(this.selectedAvatarGreenThumbnail),
        this.loadImage(this.uploadedBackgroundImage)
      ]);

      const targetWidth = 1920;
      const targetHeight = 1080; 

      // Set canvas dimensions to 16:9
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.drawImage(backgroundImg, 0, 0, canvas.width, canvas.height);
      
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
      
      if (!tempCtx) {
        console.error('Failed to get temp canvas context');
        return;
      }

      tempCanvas.width = avatarImg.width;
      tempCanvas.height = avatarImg.height;
      
      tempCtx.drawImage(avatarImg, 0, 0);
      
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;

      const greenThreshold = 100;
      const tolerance = 80; 
      
      for (let i = 0; i < data.length; i += 4) {
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];
        
        const isGreen = 
          green > greenThreshold &&
          green > red + tolerance &&
          green > blue + tolerance;
        
        if (isGreen) {
          data[i + 3] = 0;
        } else {
          const greenDiff = green - Math.max(red, blue);
          if (greenDiff > 20) {
            data[i + 1] = Math.max(0, green - greenDiff * 0.3);
          }
        }
      }

      tempCtx.putImageData(imageData, 0, 0);
      
      const scaleX = canvas.width / avatarImg.width;
      const scaleY = canvas.height / avatarImg.height;
      const scale = Math.min(scaleX, scaleY); 
      
      const scaledWidth = avatarImg.width * scale;
      const scaledHeight = avatarImg.height * scale;
      
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;
      
      // Draw the processed avatar (with transparency) centered on the 16:9 background
      ctx.drawImage(tempCanvas, x, y, scaledWidth, scaledHeight);
      this.processedImageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      this.uploadBackgroundAndThumbnail();
      
    } catch (error) {
      console.error('Error processing chroma key:', error);
    }
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = '*'; 
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
      img.src = src;
    });
  }
}
